import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Search, X } from "lucide-react";

import { DateRangePicker } from "@/components/ui/date-range-picker";
import { cn } from "@/lib/utils";
import {
  CHAT_SEARCH_DEBOUNCE_MS,
  CHAT_SEARCH_SCOPE_ALL,
  CHAT_SEARCH_SCOPE_THREAD,
} from "@/hooks/use-chat-message-search";

/**
 * The docked search bar under the conversation header (Cmd/Ctrl+F).
 *
 * The scope toggle is the whole of "search all chats": same input, same results panel, the request
 * just stops sending thread ids. Counting differs — the conversation scope gets an exact
 * "n of N" and ↑/↓ stepping, all-chats pages instead — so the counter renders per mode.
 */
export function ChatSearchBar({
  inputRef,
  query,
  onQueryChange,
  searchScope,
  onSearchScopeChange,
  allChats,
  dateRange,
  onDateRangeChange,
  results,
  total,
  activeIndex,
  loading,
  error,
  minTermLength,
  scopedThreadCount,
  onStep,
  onClose,
}) {
  // This component only mounts while the search is open, so mounting IS the moment to take focus.
  // Doing it here rather than in the opener keeps it off requestAnimationFrame, which never fires
  // in a hidden window and would leave the bar open but not typing-ready.
  useEffect(() => {
    inputRef?.current?.focus();
  }, [inputRef]);

  // The input is driven by LOCAL state and only pushes upward once typing pauses. `query` (the
  // settled term) is read by MessagesPage, which re-renders the un-virtualized message list and
  // re-runs the markdown pipeline for every bubble — doing that per keystroke made typing crawl on
  // a long conversation. Keystrokes now re-render this bar and nothing else.
  const [draft, setDraft] = useState(query);
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  // Adopt values the parent set on its own (opening the bar, clearing on close) without fighting
  // the user's in-flight typing.
  const lastPushedRef = useRef(query);
  useEffect(() => {
    if (query !== lastPushedRef.current) {
      lastPushedRef.current = query;
      setDraft(query);
    }
  }, [query]);

  useEffect(() => {
    if (draft === lastPushedRef.current) return undefined;
    const handle = setTimeout(() => {
      lastPushedRef.current = draft;
      onQueryChangeRef.current(draft);
    }, CHAT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft]);

  /** Bypass the debounce — Enter/clear should act on exactly what is on screen. */
  const flushDraft = (value) => {
    lastPushedRef.current = value;
    onQueryChangeRef.current(value);
  };

  const term = draft.trim();
  const tooShort = term.length > 0 && term.length < minTermLength;
  const hasResults = results.length > 0;
  // Typed but not yet searched.
  const pending = draft !== query && term.length >= minTermLength;
  // Exact when the server counted (conversation scope); otherwise what has been loaded so far.
  const counterTotal = Number.isFinite(total) ? total : results.length;

  const scopes = [
    {
      id: CHAT_SEARCH_SCOPE_THREAD,
      label: "This chat",
      title:
        scopedThreadCount > 1
          ? `Search the ${scopedThreadCount} conversations merged into this view`
          : "Search this conversation",
    },
    { id: CHAT_SEARCH_SCOPE_ALL, label: "All chats", title: "Search every conversation you can see" },
  ];

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Enter before the debounce has fired should search what is typed, not step through stale
      // results from the previous term.
      if (draft !== lastPushedRef.current) {
        flushDraft(draft);
        return;
      }
      onStep?.(event.shiftKey ? -1 : 1);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.();
    }
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-1.5">
      <div className="relative min-w-[12rem] flex-1">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={allChats ? "Search all conversations…" : "Search this conversation…"}
          aria-label={allChats ? "Search all conversations" : "Search this conversation"}
          className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        {loading ? (
          <Loader2
            className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
            aria-hidden
          />
        ) : draft ? (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              flushDraft("");
              inputRef?.current?.focus();
            }}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      {/* h-8 on every control in this row (input, scope toggle, date picker) so they line up as one
          bar. Without an explicit height the toggle sized to its text and sat 2-6px short. */}
      <div
        role="tablist"
        aria-label="Search scope"
        className="flex h-8 shrink-0 items-center rounded-lg border border-border bg-card p-0.5 shadow-sm"
      >
        {scopes.map((item) => {
          const active = searchScope === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              title={item.title}
              onClick={() => onSearchScopeChange(item.id)}
              className={cn(
                // h-full + centring instead of vertical padding, so both segments fill the row
                // exactly whatever height the container is given.
                "inline-flex h-full items-center rounded-md px-2.5 text-xs leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-indigo-600 font-semibold text-white shadow-sm dark:bg-indigo-500"
                  : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {/* DateRangePicker is used directly rather than nested inside another popover: wrapping it
          cost two clicks to reach the calendar, and its own trigger already opens one. */}
      <div className="flex shrink-0 items-center gap-1">
        <DateRangePicker
          range={dateRange}
          setRange={onDateRangeChange}
          placeholder="Any date"
          align="end"
          className={cn(
            "h-8 w-auto min-w-[6.5rem] justify-center gap-1.5 px-2.5 text-xs shadow-none",
            dateRange?.from
              ? "border-indigo-500/50 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
              : "bg-card",
          )}
        />
        {dateRange?.from ? (
          <button
            type="button"
            onClick={() => onDateRangeChange(null)}
            title="Clear date filter"
            aria-label="Clear date filter"
            className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <span
          className="min-w-[4.5rem] text-right text-xs tabular-nums text-muted-foreground"
          aria-live="polite"
        >
          {/* `pending` covers the debounce window: the counter belongs to the previous term until
              the new one is searched, so show progress rather than a stale or "no matches" count. */}
          {pending
            ? "Searching…"
            : error
            ? "Search failed"
            : tooShort
              ? `${minTermLength}+ characters`
              : !term
                ? ""
                : loading && !hasResults
                  ? "Searching…"
                  : !hasResults
                    ? "No matches"
                    : allChats
                      ? `${counterTotal}${results.length >= counterTotal ? "" : "+"} found`
                      : `${activeIndex + 1} of ${counterTotal}`}
        </span>
        {/* Stepping is a conversation-scope affordance: an all-chats hit lives in a thread that is
            not open, so "previous/next" would mean walking the reader out of the conversation. */}
        {!allChats ? (
          <>
            <button
              type="button"
              disabled={!hasResults || activeIndex <= 0}
              onClick={() => onStep(-1)}
              title="Newer match (Shift+Enter)"
              aria-label="Newer match"
              className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronUp className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              disabled={!hasResults || activeIndex >= results.length - 1}
              onClick={() => onStep(1)}
              title="Older match (Enter)"
              aria-label="Older match"
              className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown className="h-4 w-4" aria-hidden />
            </button>
          </>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          title="Close search (Esc)"
          aria-label="Close search"
          className="ml-0.5 rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
